import React from "react";
import { NumberInput, Textarea } from "../../atoms";
import { buildFirmCreatePayload } from "../../../utils/firmCreateForm";
import { getFirmTypeStyle } from "../../../utils/firmTypeStyles";

const Row = ({ label, children }) => (
  <div className="flex justify-between gap-4 py-1.5">
    <span className="text-gray-500 shrink-0">{label}</span>
    <span className="text-gray-900 text-right break-words min-w-0">{children}</span>
  </div>
);

const Section = ({ title, step, onEditStep, children }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-4">
    <div className="flex items-center justify-between mb-1">
      <h4 className="text-sm font-SemiBold text-gray-700">{title}</h4>
      <button
        type="button"
        onClick={() => onEditStep(step)}
        className="text-xs font-Medium text-blue-600 hover:underline"
      >
        Düzenle
      </button>
    </div>
    <div className="text-sm divide-y divide-gray-100">{children}</div>
  </div>
);

/**
 * Firma oluşturma — 4. adım: sipariş sıklığı, not ve gönderilecek verinin
 * özeti (#275). Özet buildFirmCreatePayload'dan okunur (varsayılan tel/e-posta).
 *
 * @param {boolean} productsAvailable ürün listesi alınabildi mi
 */
export default function FirmSettingsReviewStep({
  input,
  errors,
  onChange,
  onEditStep,
  productsAvailable,
}) {
  const payload = buildFirmCreatePayload(input);
  const address = [input.address.text, input.address.town, input.address.city]
    .filter(Boolean)
    .join(", ");

  return (
    <div className="space-y-6">
      <div className="bg-gray-50 rounded-lg p-4 space-y-4">
        <h3 className="text-base font-SemiBold text-gray-800">Ayarlar</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <NumberInput
            label="Sipariş Sıklığı (Gün)"
            placeholder="30"
            value={input.orderFrequencyDays}
            onChange={(value) => onChange("orderFrequencyDays", value)}
            min={1}
            max={360}
            error={!!errors.orderFrequencyDays}
            errorMessage={errors.orderFrequencyDays}
          />
        </div>
        <Textarea
          label="Not"
          placeholder="Ek notlar..."
          value={input.note}
          onChange={(e) => onChange("note", e.target.value)}
          rows={3}
        />
      </div>

      <div className="space-y-3">
        <h3 className="text-base font-SemiBold text-gray-800">Özet</h3>
        <Section title="Firma" step={1} onEditStep={onEditStep}>
          <Row label="Firma Adı">{input.firmName}</Row>
          <Row label="Tip">
            {getFirmTypeStyle(input).label} · {input.isCorporate ? "Kurumsal" : "Bireysel"}
          </Row>
          {input.dealerFirm?.firmName && <Row label="Bayi">{input.dealerFirm.firmName}</Row>}
          {payload.mainFirmName !== "none" && (
            <Row label={input.firmType === "dealer" ? "Ana Bayi" : "Ana Firma"}>
              {payload.mainFirmName}
            </Row>
          )}
          {payload.ownerName && <Row label="Firma Sahibi">{payload.ownerName}</Row>}
        </Section>

        <Section title="İletişim" step={2} onEditStep={onEditStep}>
          <Row label="Email">{payload.email}</Row>
          <Row label="Telefon">{payload.tel}</Row>
          {payload.contacts.length > 0 && (
            <Row label="Muhataplar">
              {payload.contacts.map((c) => (c.tel ? `${c.name} · ${c.tel}` : c.name)).join(", ")}
            </Row>
          )}
          <Row label="Adres">{address}</Row>
          {input.isCorporate && (
            <Row label="Vergi">
              {input.taxNumber} · {input.taxOffice} · {input.officialNumber}
            </Row>
          )}
        </Section>

        <Section title="Ürünler" step={3} onEditStep={onEditStep}>
          <Row label="Aktif">
            {productsAvailable ? (payload.activeProducts || []).join(", ") : "Tüm ürünler"}
          </Row>
        </Section>
      </div>
    </div>
  );
}
